import { useState, useRef, useEffect } from "react";

export default function useMainVideoPlayer({
  initialLoading,
  onActiveVideoReady,
}) {
  const mainVideoRef = useRef(null);
  const mainVideoContainerRef = useRef(null);

  // Ref для отслеживания пользовательских действий
  const userPausedRef = useRef(false);

  // Состояние активного видео для контролов
  const [activeVideoData, setActiveVideoData] = useState(null);
  const [videoState, setVideoState] = useState({
    status: "loading",
    duration: 0,
    currentTime: 0,
    buffered: [],
    isUserPause: false,
    volume: 1,
    muted: true, // 🔴 Исправление: muted = true для autoplay
    isLoading: false,
    hlsReady: false,
  });

  // Действия для управления главным видео
  const videoActions = {
    play: () => {
      console.log("videoActions.play() called");
      if (mainVideoRef.current) {
        userPausedRef.current = false; // Сбрасываем флаг пользовательской паузы
        console.log("Calling mainVideoRef.current.play()");
        mainVideoRef.current.play().catch(console.error);
      }
    },
    pause: () => {
      console.log("videoActions.pause() called");
      if (mainVideoRef.current) {
        userPausedRef.current = true; // Устанавливаем флаг пользовательской паузы
        console.log("Calling mainVideoRef.current.pause()");
        mainVideoRef.current.pause();
      }
    },
    seek: (time) => {
      if (mainVideoRef.current) {
        mainVideoRef.current.currentTime = time;
      }
    },
    navigate: (time) => {
      // Алиас для seek - для совместимости с ShortySeekBar
      if (mainVideoRef.current) {
        mainVideoRef.current.currentTime = time;
      }
    },
    setVolume: (volume) => {
      if (mainVideoRef.current) {
        mainVideoRef.current.volume = volume;
      }
    },
    setMuted: (muted) => {
      if (mainVideoRef.current) {
        mainVideoRef.current.muted = muted;
      }
    },
    // 🔴 Исправление: добавляем toggleMute для ShortyControlPanel
    toggleMute: () => {
      if (mainVideoRef.current) {
        mainVideoRef.current.muted = !mainVideoRef.current.muted;
      }
    },
  };

  // Callback для получения данных активного видео
  const handleActiveVideoReady = (videoData) => {
    let isNewVideo = false;

    // Обновляем только если данные действительно изменились
    setActiveVideoData((prev) => {
      if (prev?.content?.id === videoData?.content?.id) {
        return prev;
      }
      isNewVideo = true; // Помечаем что это новое видео
      return videoData;
    });

    // Вызываем внешний callback если передан
    if (onActiveVideoReady) {
      onActiveVideoReady(videoData);
    }

    // Автозапуск только для нового видео
    if (isNewVideo) {
      setTimeout(() => {
        if (mainVideoRef.current && !userPausedRef.current) {
          // Проверяем состояние видео элемента
          const isAlreadyPlaying = !mainVideoRef.current.paused;
          const isReadyToPlay = mainVideoRef.current.readyState >= 2; // HAVE_CURRENT_DATA

          if (isAlreadyPlaying) {
            console.log("Auto-start skipped: video already playing");
            return;
          }

          if (!isReadyToPlay) {
            console.log("Auto-start skipped: video not ready");
            return;
          }

          console.log("Auto-starting video (user has not paused)");
          mainVideoRef.current.play().catch((err) => {
            console.log(
              "Autoplay failed - user interaction required:",
              err.message
            );
          });
        } else if (userPausedRef.current) {
          console.log("Skipping autoplay - user has paused the video");
        } else {
          console.log("Skipping autoplay - video not ready");
        }
      }, 300); // Увеличиваем задержку для iOS
    }
  };

  // Создание главного video элемента
  useEffect(() => {
    if (initialLoading) {
      return;
    }

    if (typeof document === "undefined" || !mainVideoContainerRef.current) {
      return;
    }

    // Если video уже создан, не создаваем повторно
    if (mainVideoRef.current) {
      return;
    }

    const video = document.createElement("video");
    video.muted = true; // Для autoplay на iOS
    video.playsInline = true;
    video.style.width = "100%";
    video.style.height = "100%";
    video.style.objectFit = "cover";
    video.style.zIndex = "0"; // Видео под контролами
    video.autoplay = true;
    video.preload = "auto";
    video.id = "main-video";
    video.loop = true; // HLS управляет зацикливанием сам

    // Event handlers с throttling для timeupdate
    const handleLoadedMetadata = () => {
      setVideoState((prev) => ({
        ...prev,
        duration: video.duration,
        status: "ready",
      }));
    };

    const handleTimeUpdate = () => {
      const now = Date.now();
      if (video._lastTimeUpdate && now - video._lastTimeUpdate < 250) {
        return;
      }
      video._lastTimeUpdate = now;

      const buffered = [];
      for (let i = 0; i < video.buffered.length; i++) {
        buffered.push({
          start: video.buffered.start(i),
          end: video.buffered.end(i),
        });
      }

      setVideoState((prev) => {
        // Если currentTime растет и видео не на паузе, значит оно играет
        const isActuallyPlaying = !video.paused && video.currentTime > 0;
        const shouldBePlayingStatus =
          isActuallyPlaying && prev.status !== "playing";

        return {
          ...prev,
          currentTime: video.currentTime,
          buffered,
          // Обновляем статус если видео фактически играет, но статус не "playing"
          status: shouldBePlayingStatus ? "playing" : prev.status,
        };
      });
    };

    const handlePlay = () => {
      console.log("Video play event fired, userPaused:", userPausedRef.current);
      setVideoState((prev) => ({
        ...prev,
        status: "playing",
        isLoading: false,
      }));
    };

    const handlePause = () => {
      console.log(
        "Video pause event fired, userPaused:",
        userPausedRef.current
      );
      setVideoState((prev) => ({ ...prev, status: "paused" }));
    };

    const handleEnded = () => {
      setVideoState((prev) => ({ ...prev, status: "ended" }));
    };

    const handleVolumeChange = () => {
      setVideoState((prev) => ({
        ...prev,
        volume: video.volume,
        muted: video.muted,
      }));
    };

    const handleWaiting = () => {
      setVideoState((prev) => ({ ...prev, isLoading: true }));
    };

    const handleCanPlay = () => {
      setVideoState((prev) => ({ ...prev, isLoading: false }));
    };

    // Добавляем event listeners
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("volumechange", handleVolumeChange);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);

    // Сохраняем handlers для cleanup
    video._eventHandlers = {
      handleLoadedMetadata,
      handleTimeUpdate,
      handlePlay,
      handlePause,
      handleEnded,
      handleVolumeChange,
      handleWaiting,
      handleCanPlay,
    };

    // Устанавливаем ref и добавляем в DOM
    mainVideoRef.current = video;
    mainVideoContainerRef.current.appendChild(video);

    return () => {
      if (mainVideoRef.current) {
        const video = mainVideoRef.current;
        const handlers = video._eventHandlers;

        if (handlers) {
          video.removeEventListener(
            "loadedmetadata",
            handlers.handleLoadedMetadata
          );
          video.removeEventListener("timeupdate", handlers.handleTimeUpdate);
          video.removeEventListener("play", handlers.handlePlay);
          video.removeEventListener("pause", handlers.handlePause);
          video.removeEventListener("ended", handlers.handleEnded);
          video.removeEventListener(
            "volumechange",
            handlers.handleVolumeChange
          );
          video.removeEventListener("waiting", handlers.handleWaiting);
          video.removeEventListener("canplay", handlers.handleCanPlay);
        }

        if (video.parentNode) {
          video.remove();
        }
        mainVideoRef.current = null;
      }
    };
  }, [initialLoading]); // Выполняется только один раз при монтировании

  return {
    mainVideoRef,
    mainVideoContainerRef,
    userPausedRef,
    activeVideoData,
    videoState,
    videoActions,
    handleActiveVideoReady,
  };
}
